import { useState, useEffect } from 'react';
import { getAllAccountsByEmail } from '@/actions/switch';
import { authService } from '@/utils/auth/authService';
import SwitchAccountClient from './SwitchAccountClient';

interface UserData {
    client_uuid: string;
    email: string;
    [key: string]: any;
}

interface ClientInfo {
    uuid: string;
    name: string;
    email: string;
    phone: string;
    image: string | null;
    status: string;
}

interface RoleInfo {
    uuid: string;
    name: string;
    level: number;
}

interface UserInfo {
    uuid: string;
    name: string;
    email: string;
    role: RoleInfo;
}

interface AccountClient {
    client: ClientInfo;
    user: UserInfo;
}

const SwitchAccount = () => {
    const [accounts, setAccounts] = useState<AccountClient[] | null>(null);
    const [user, setUser] = useState<UserData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [previousAccount, setPreviousAccount] = useState<any>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // Get user data from authService (localStorage)
                const userData = authService.getUser();
                console.log('User data from authService:', userData);

                if (!userData) {
                    setError('Please log in to view your accounts');
                    return;
                }

                // Handle nested data structure
                const user = (userData as any).data || userData;
                console.log('Processed user:', user);

                setUser(user as UserData);
                const email = user.userEmail || user.email;

                if (!email) {
                    setError('User email not found');
                    console.error('User object:', user);
                    return;
                }

                // Check if there's a previous account (user switched from another account)
                const prevAccount = authService.getPreviousAccount();
                if (prevAccount) {
                    console.log('📌 Previous account found:', prevAccount);
                    setPreviousAccount(prevAccount);
                }

                // Fetch accounts by email
                const accountsData = await getAllAccountsByEmail({ email });
                console.log('Accounts data:', accountsData);

                // Access the accounts array from the data structure
                const accountsList = (accountsData as any)?.data?.accounts || [];
                setAccounts(accountsList);

            } catch (err) {
                console.error("Error in SwitchAccount:", err);
                setError('Error loading accounts. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) {
        return (
            <div className="px-6 pb-6">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 p-6 rounded-lg">
                    <p className="text-blue-800 dark:text-blue-200">Loading accounts...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="px-6 pb-6">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 p-6 rounded-lg">
                    <p className="text-red-800 dark:text-red-200">{error}</p>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="px-6 pb-6">
                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 rounded-lg">
                    <p className="text-gray-700 dark:text-gray-300">Please log in to view your accounts</p>
                </div>
            </div>
        );
    }

    return (
        <div className="px-6 pb-6">
            {/* Show "Return to Previous Account" banner if user switched from another account */}
            {previousAccount && previousAccount.client_uuid !== user?.client_uuid && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 p-4 rounded-lg mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                                📌 You switched from another account
                            </p>
                            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                                Previously: <span className="font-medium">{previousAccount.client_name}</span>
                            </p>
                        </div>
                        <button
                            onClick={async () => {
                                try {
                                    await authService.returnToPreviousAccount(user?.userEmail || user?.email);
                                    window.location.replace('/leads');
                                } catch (error) {
                                    console.error('Error returning to previous account:', error);
                                    alert('Failed to return to previous account');
                                }
                            }}
                            className="ml-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                            Return to Previous Account
                        </button>
                    </div>
                </div>
            )}

            {accounts && accounts.length > 0 ? (
                <SwitchAccountClient
                    clientsList={accounts}
                    currentClientUuid={user.client_uuid}
                    userEmail={user.userEmail || user.email}
                />
            ) : (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 p-6 rounded-lg">
                    <p className="text-yellow-800 dark:text-yellow-200">No additional accounts found for this email address.</p>
                </div>
            )}
        </div>
    );
};

export default SwitchAccount;